import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, ListChecks, CalendarCheck, Layers, History, UserCircle, Shield, LogOut, Menu, MessagesSquare, Trophy, Award, Inbox, Briefcase, Crown } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LINKS = [
  ["/dashboard", "Dashboard", LayoutDashboard],
  ["/learn", "AI Learning Assistant", Sparkles],
  ["/quiz", "Quiz Generator", ListChecks],
  ["/daily", "Daily Quiz", CalendarCheck],
  ["/flashcards", "Flashcards", Layers],
  ["/interview", "Interview Prep", MessagesSquare],
  ["/leaderboard", "Leaderboard", Trophy],
  ["/badges", "Badges", Award],
  ["/history", "Quiz History", History],
  ["/jobs", "Jobs", Briefcase],
  ["/messages", "Messages", Inbox],
  ["/profile", "Profile", UserCircle],
  ["/pricing", "Plans", Crown],
];

function useUnread() {
  const [n, setN] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    const load = () => api.get("/messages/unread-count").then((r) => setN(r.data.unread)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("messages-updated", load);
    return () => { clearInterval(t); window.removeEventListener("messages-updated", load); };
  }, [pathname]);
  return n;
}

function NavItems({ onNavigate }) {
  const { user, logout } = useAuth();
  const unread = useUnread();
  const nav = useNavigate();
  const links = user?.role === "admin" ? [...LINKS, ["/admin", "Admin", Shield]] : LINKS;
  return (
    <div className="flex h-full flex-col">
      <nav className="flex flex-col gap-1">
        {links.map(([to, label, Icon]) => (
          <NavLink key={to} to={to} onClick={onNavigate} data-testid={`nav-${to.slice(1)}`}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-solid/15 font-semibold text-ink shadow-[inset_3px_0_0_rgb(var(--c-aqua))]" : "text-ink-2 hover:bg-elevated hover:text-ink"}`}>
            <Icon className="h-4 w-4" />{label}
            {to === "/messages" && unread > 0 && <span data-testid="nav-messages-unread" className="ml-auto min-w-5 rounded-full bg-aqua-solid px-1.5 py-0.5 text-center text-[11px] font-bold leading-none text-white">{unread > 99 ? "99+" : unread}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto border-t border-line pt-4">
        <p data-testid="sidebar-user-name" className="truncate px-3 text-sm font-semibold text-ink">{user?.name}</p>
        <p className="truncate px-3 text-xs text-ink-3">{user?.email}</p>
        <button data-testid="logout-btn" onClick={async () => { await logout(); nav("/"); }}
          className="mt-3 flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-ink-2 hover:bg-red-50 hover:text-red-700">
          <LogOut className="h-4 w-4" />Log out
        </button>
      </div>
    </div>
  );
}

export default function AppLayout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  return (
    <div className="min-h-screen bg-canvas">
      <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-line bg-canvas-2 px-4 py-6 lg:flex">
        <div className="mb-8 flex items-center justify-between gap-2 px-2"><Logo to="/dashboard" className="h-10" /><AppearanceMenu /></div>
        <NavItems />
      </aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-line bg-canvas/90 px-4 py-3 backdrop-blur lg:hidden">
        <Logo to="/dashboard" className="h-8" />
        <div className="flex items-center gap-1"><AppearanceMenu />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-btn" className="rounded-md p-2 hover:bg-elevated" aria-label="Open menu"><Menu className="h-5 w-5" /></button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-canvas-2 p-5 pt-12"><NavItems onNavigate={() => setOpen(false)} /></SheetContent>
        </Sheet></div>
      </header>
      <main className="px-4 py-8 sm:px-8 lg:ml-64 lg:px-12 lg:py-12">
        <div className="mx-auto max-w-6xl"><Outlet key={location.key} /></div>
      </main>
    </div>
  );
}
